import { motion } from "framer-motion";
import { Check, MessageCircle, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../../constants/site";
import { Eyebrow } from "./Reveal";

const HEADLINE = ["Bug sweeps for boardrooms,", "deal rooms and confidential meetings."];

const PROOF = [
  "Led by Rajiv Vermani, founder and lead practitioner.",
  "Real instruments: spectrum analysis, non-linear junction detection, thermal imaging, telephone-line analysis, and a physical search of the room.",
  "Since 2005. From the company that builds surveillance technology for India's defence and law enforcement.",
];

// Full-bleed hero: the inspection photo runs behind everything, graded from
// solid dark on the text side to fully visible on the right, where Rajiv sits.
export default function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 hidden lg:block">
        <img
          src="/media/hero-oscor-green-wide.jpg"
          alt="Rajiv Vermani operating a spectrum analyser at a boardroom table"
          className="h-full w-full object-cover object-right"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--bg)_0%,var(--bg)_24%,rgb(19_17_24/0.82)_36%,rgb(19_17_24/0.38)_48%,rgb(19_17_24/0.1)_60%,transparent_72%)]" />
      </div>

      <div className="relative site-x flex flex-col justify-center py-16 sm:py-20 lg:min-h-[80vh] lg:py-24">
        <div className="max-w-xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            <Eyebrow>Bug sweeps (TSCM) · anywhere in India</Eyebrow>
          </motion.div>

          <h1 className="mt-5 font-head font-extrabold leading-[1.08] tracking-tight h-hero">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-violet" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-6 max-w-xl text-lg leading-[1.9] text-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
          >
            Before a board, audit committee, investor or deal meeting, we check
            the room for hidden microphones and cameras.
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap items-center gap-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.74, ease: [0.22, 1, 0.36, 1] }}
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full bg-violet px-6 py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
            >
              <MessageCircle size={15} />
              WhatsApp us
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-6 py-3 text-[0.9375rem] font-semibold transition-colors hover:border-violet hover:text-violet"
            >
              <Phone size={14} />
              Call {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.ul
            className="mt-9 hidden max-w-lg flex-col gap-3 sm:flex"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.86, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-proof-points"
          >
            {PROOF.map((point) => (
              <li key={point} className="flex items-start gap-3 text-[0.9375rem] leading-relaxed text-sub">
                <Check size={15} className="mt-1 shrink-0 text-violet" />
                <span>{point}</span>
              </li>
            ))}
            <li className="flex items-start gap-3 text-[0.9375rem] leading-relaxed text-sub">
              <Check size={15} className="mt-1 shrink-0 text-violet" />
              <span>
                A report your board can read. Sealed, delivered only to the
                person who engaged us.
              </span>
            </li>
          </motion.ul>

          <motion.p
            className="mt-8 text-sm leading-relaxed text-sub sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.86 }}
            data-testid="hero-proof-mobile"
          >
            Led by Rajiv Vermani · since 2005
          </motion.p>
        </div>
      </div>

      <div className="relative site-x pb-14 lg:hidden">
        <div className="photo-frame border border-hairline bg-raised p-2.5">
          <img
            src="/media/hero-oscor-green-wide.jpg"
            alt="Rajiv Vermani operating a spectrum analyser at a boardroom table"
            className="h-auto w-full"
            data-testid="hero-image-mobile"
          />
        </div>
        <p className="mt-4 border-t border-hairline pt-3 text-sm uppercase leading-relaxed tracking-[0.16em] text-sub">
          Rajiv Vermani during an authorised boardroom inspection
        </p>
      </div>

      <p
        className="absolute bottom-4 right-5 hidden rounded bg-base-color/50 px-2.5 py-1 text-xs uppercase tracking-[0.16em] text-ink/75 backdrop-blur-sm lg:block"
        data-testid="hero-caption"
      >
        Rajiv Vermani during an authorised boardroom inspection
      </p>
    </section>
  );
}
